import { useState, useEffect, useCallback } from 'react'
import { GoogleGenAI } from '@google/genai'
import { detectCannedResponse } from './cannedResponses'
import {
  getGeminiApiKey,
  getGeminiModel,
  getGeminiSystemPrompt
} from '../ChatHeader/AiApiKeySettingsModal'
import { match } from 'ts-pattern'
import {
  getRevenueAnalysisDescription,
  getTableSelectionLabel
} from '../ChatInputBox/helpers'
import type { TableSelectionData } from '../useAutoContextSelection'
import { GenerativeComponentData } from '../generativeComponents'
import { generativeUIContext } from './systemPromptV0'
import { fetchAllForCustomer } from './fetchCustomerEnrichment'
import { toMoney } from '@sequencehq/utils'
import { Currency } from '@sequencehq/api'

const formatCurrency = (
  value: number | undefined,
  currency?: string | Currency,
  fallback = '0'
) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return fallback
  }

  const resolvedCurrency = (currency || 'USD') as Currency
  return toMoney({
    value: value.toString(),
    currency: resolvedCurrency
  })
}

// Helper function to get detailed information about selected context items
export const getDetailedContextInfo = (
  selectedItems: Set<string>,
  customers: any[] = [],
  products: any[] = [],
  tableSelection?: any
) => {
  const details: Array<{
    type: string
    id: string
    data: any
  }> = []

  selectedItems.forEach(item => {
    const detail = match(item)
      .when(
        item => item.startsWith('customer:'),
        item => {
          const customerId = item.replace('customer:', '')
          return match(customerId)
            .with('all', () => ({
              type: 'customer_group' as const,
              id: 'all',
              data: {
                label: 'All Customers',
                count: customers.length,
                summary: `All ${customers.length} customers in the system`
              }
            }))
            .with('top10', () => ({
              type: 'customer_group' as const,
              id: 'top10',
              data: {
                label: 'Top 10 Customers',
                count: Math.min(10, customers.length),
                summary: `Top 10 customers by revenue performance`
              }
            }))
            .otherwise(() => {
              const customer = customers.find(c => c.id === customerId)
              return customer
                ? {
                    type: 'customer' as const,
                    id: customerId,
                    data: {
                      id: customer.id,
                      legalName: customer.legalName,
                      contactName: customer.contactName,
                      label: customer.label,
                      domain: customer.domain
                      // Add any other relevant customer fields
                    }
                  }
                : null
            })
        }
      )
      .when(
        item => item.startsWith('product:'),
        item => {
          const productId = item.replace('product:', '')
          return match(productId)
            .with('all', () => ({
              type: 'product_group' as const,
              id: 'all',
              data: {
                label: 'All Products',
                count: products.length,
                summary: `All ${products.length} products in the system`
              }
            }))
            .otherwise(() => {
              const product = products.find(p => p.id === productId)
              return product
                ? {
                    type: 'product' as const,
                    id: productId,
                    data: {
                      id: product.id,
                      name: product.name,
                      label: product.label
                      // Add other relevant product fields
                    }
                  }
                : null
            })
        }
      )
      .when(
        item => item.startsWith('revenue:'),
        item => {
          const revenueType = item.replace('revenue:', '')
          return {
            type: 'revenue_analysis' as const,
            id: revenueType,
            data: {
              type: revenueType,
              description: getRevenueAnalysisDescription(revenueType)
            }
          }
        }
      )
      .when(
        item => item.startsWith('table-selection:'),
        () => {
          // Handle table selection context items
          return tableSelection
            ? {
                type: 'table_selection' as const,
                id: 'current',
                data: {
                  label: getTableSelectionLabel(tableSelection),
                  rowCount: tableSelection.rowCount,
                  columnCount: tableSelection.columnCount,
                  rowLabels: tableSelection.rowLabels,
                  columnLabels: tableSelection.columnLabels,
                  cellData: tableSelection.cellData,
                  summary: `Selected ${tableSelection.rowCount} rows × ${tableSelection.columnCount} columns from revenue waterfall`
                }
              }
            : null
        }
      )
      .otherwise(() => null)

    if (detail) {
      details.push(detail)
    }
  })

  return details
}

const getApiKey = () => {
  const savedKey = getGeminiApiKey()
  return savedKey
}

const getSelectedModel = () => {
  return getGeminiModel()
}

const getCustomSystemPrompt = () => {
  return getGeminiSystemPrompt()
}

export type StatusType =
  | 'idle'
  | 'processing'
  | 'generating'
  | 'searching'
  | 'looking'
  | 'error'

export type Message = {
  id: string
  role: 'user' | 'assistant'
  content: string
  createdAt: Date
  component?: GenerativeComponentData
  components?: GenerativeComponentData[]
  type?: 'text' | 'component'
}

type ContextData = {
  selectedContextItems?: Set<string>
  revenueWaterfallData?: any
  revenueWaterfallFilters?: any
  currency?: string
  // Additional data for detailed context
  customers?: any[]
  products?: any[]
  tableSelection?: TableSelectionData | null
}

const getFocusedCustomer = (
  contextData?: ContextData
): { id?: string; name?: string } => {
  if (!contextData) return {}
  const tableSelection = contextData.tableSelection
  if (tableSelection) {
    const foundCustomer = tableSelection.cellData.find(
      c => c.rowData?.type === 'customer' && c.rowData.id
    )

    if (foundCustomer) {
      return {
        id: foundCustomer.rowData.id,
        name: foundCustomer.rowData.label
      }
    }
  }
  return {}
}

const formatContextForLLM = (contextData?: ContextData) => {
  if (!contextData) return ''

  let contextString = '\n\n## Current Context\n'

  // Add revenue waterfall data if available
  if (contextData.revenueWaterfallData) {
    const { data } = contextData.revenueWaterfallData
    if (data) {
      contextString += `\n### Revenue Waterfall Summary\n`
      contextString += `- Currency: ${contextData.currency || 'GBP'}\n`
      contextString += `- Total Booked Revenue: ${formatCurrency(
        data.totals?.bookedTotal,
        contextData.currency,
        'N/A'
      )}\n`
      contextString += `- Total Recognized Revenue: ${formatCurrency(
        data.totals?.recognized,
        contextData.currency,
        'N/A'
      )}\n`
      contextString += `- Total Remaining Revenue: ${formatCurrency(
        data.totals?.remaining,
        contextData.currency,
        'N/A'
      )}\n`
      contextString += `- Monthly Columns: ${data.monthlyColumns?.join(', ') || 'N/A'}\n`

      if (contextData.revenueWaterfallFilters) {
        const filters = contextData.revenueWaterfallFilters
        contextString += `\n### Current Filters\n`
        contextString += `- Booked From: ${filters.bookedFrom}\n`
        contextString += `- Booked To: ${filters.bookedTo}\n`
        contextString += `- Recognized Up To: ${filters.recognizedUpTo}\n`
        contextString += `- Primary Grouping: ${filters.grouping}\n`
        contextString += `- Sub Grouping: ${filters.subGrouping}\n`
      }

      // Add detailed revenue breakdown by rows
      const flatRows = data.flatRows ?? []

      if (flatRows.length > 0) {
        contextString += `\n### Detailed Revenue Breakdown\n`

        const mainGroups = flatRows.filter((row: any) => row.depth === 0)
        const subRowMap = new Map<string, any[]>()

        flatRows.forEach((row: any) => {
          if (row.depth > 0 && row.parentInternalId) {
            const entries = subRowMap.get(row.parentInternalId) ?? []
            entries.push(row)
            subRowMap.set(row.parentInternalId, entries)
          }
        })

        mainGroups.forEach((mainRow: any) => {
          contextString += `\n#### ${mainRow.label} (Main Group)\n`
          contextString += `- Total Booked: ${formatCurrency(
            mainRow.bookedTotal,
            contextData.currency
          )}\n`
          contextString += `- Total Recognized: ${formatCurrency(
            mainRow.recognized,
            contextData.currency
          )}\n`
          contextString += `- Remaining: ${formatCurrency(
            mainRow.remaining,
            contextData.currency
          )}\n`

          // Add monthly breakdown for main group
          if (mainRow.monthlyValues && data.monthlyColumns) {
            contextString += `- Monthly Breakdown:\n`
            data.monthlyColumns.forEach((month: string) => {
              const monthValue = mainRow.monthlyValues[month]
              if (typeof monthValue === 'number' && monthValue > 0) {
                contextString += `  • ${month}: ${formatCurrency(
                  monthValue,
                  contextData.currency
                )}\n`
              }
            })
          }

          // Add sub-rows for this main group
          const relatedSubRows = subRowMap.get(mainRow.internalId) ?? []
          if (relatedSubRows.length > 0) {
            contextString += `- Sub-items:\n`
            relatedSubRows.forEach((subRow: any) => {
              contextString += `  • ${subRow.label}: Booked ${formatCurrency(
                subRow.bookedTotal,
                contextData.currency
              )}, Recognized ${formatCurrency(
                subRow.recognized,
                contextData.currency
              )}, Remaining ${formatCurrency(
                subRow.remaining,
                contextData.currency
              )}\n`

              // Add monthly breakdown for sub-rows if significant
              if (subRow.monthlyValues && data.monthlyColumns) {
                const significantMonths = data.monthlyColumns.filter(
                  (month: string) => {
                    const value = subRow.monthlyValues[month]
                    return typeof value === 'number' && value > 0
                  }
                )
                if (
                  significantMonths.length > 0 &&
                  significantMonths.length <= 3
                ) {
                  contextString += `    Monthly: ${significantMonths
                    .map(
                      (month: string) =>
                        `${month}: ${formatCurrency(
                          subRow.monthlyValues[month],
                          contextData.currency
                        )}`
                    )
                    .join(', ')}\n`
                }
              }
            })
          }
        })

        // Add monthly totals breakdown
        if (data.totals?.monthlyTotals && data.monthlyColumns) {
          contextString += `\n### Monthly Totals Across All Customers\n`
          data.monthlyColumns.forEach((month: string) => {
            const monthTotal = data.totals.monthlyTotals[month]
            if (typeof monthTotal === 'number' && monthTotal > 0) {
              contextString += `- ${month}: ${formatCurrency(
                monthTotal,
                contextData.currency
              )}\n`
            }
          })
        }
      }
    }
  }

  // Add table selection data if available
  if (contextData.tableSelection) {
    const selection = contextData.tableSelection
    contextString += `\n### Table Selection Context\n`
    contextString += `- Selection: ${selection.rowCount} rows × ${selection.columnCount} columns\n`
    contextString += `- Rows: ${selection.rowLabels.join(', ')}\n`
    contextString += `- Columns: ${selection.columnLabels.join(', ')}\n`

    if (selection.cellData.length > 0) {
      contextString += `\n#### Selected Cell Data:\n`
      selection.cellData.forEach(cell => {
        contextString += `- ${cell.rowLabel} | ${cell.columnLabel}: ${cell.value}\n`
      })
    }

    // Add detailed row information using the waterfall data
    if (contextData.revenueWaterfallData?.data?.flatRows) {
      const waterfallRows = contextData.revenueWaterfallData.data.flatRows
      const selectedRowIndices = new Set(
        selection.cellData.map(cell => cell.row)
      )

      contextString += `\n#### Detailed Row Analysis:\n`
      selectedRowIndices.forEach(rowIndex => {
        const rowData = waterfallRows[rowIndex]
        if (rowData) {
          contextString += `- Row ${rowIndex} (${rowData.label}):\n`
          if (rowData.customerId) {
            contextString += `  • Customer ID: ${rowData.customerId}\n`
          }
          if (rowData.type) {
            contextString += `  • Type: ${rowData.type}\n`
          }
          if (rowData.depth > 0 && rowData.parentInternalId) {
            contextString += `  • Child of: ${rowData.parentInternalId}\n`
          }
          if (rowData.bookedTotal) {
            contextString += `  • Total Booked: ${formatCurrency(
              rowData.bookedTotal,
              contextData.currency
            )}\n`
          }
          if (rowData.recognized) {
            contextString += `  • Recognized: ${formatCurrency(
              rowData.recognized,
              contextData.currency
            )}\n`
          }
          if (rowData.remaining) {
            contextString += `  • Remaining: ${formatCurrency(
              rowData.remaining,
              contextData.currency
            )}\n`
          }
          if (rowData.scheduleId) {
            contextString += `  • Schedule ID: ${rowData.scheduleId}\n`
          }
        }
      })
    }

    contextString += `\n*Note: The user has specifically selected this data from the revenue waterfall table for analysis.*\n`
  }

  // Add detailed selected context items if available
  if (
    contextData.selectedContextItems &&
    contextData.selectedContextItems.size > 0
  ) {
    const detailedContext = getDetailedContextInfo(
      contextData.selectedContextItems,
      contextData.customers || [],
      contextData.products || [],
      contextData.tableSelection
    )

    if (detailedContext.length > 0) {
      contextString += `\n### Selected Context Items (User Focus)\n`
      detailedContext.forEach(item => {
        contextString += `\n#### ${item.type.toUpperCase()}: ${item.id}\n`

        if (item.type === 'customer') {
          const data = item.data
          contextString += `- Company: ${data.legalName}\n`
          contextString += `- Contact: ${data.contactName || 'N/A'}\n`
          contextString += `- Domain: ${data.domain || 'N/A'}\n`
          contextString += `- ID: ${data.id}\n`

          // Try to find revenue data for this specific customer
          if (contextData.revenueWaterfallData?.data?.flatRows) {
            const customerRows =
              contextData.revenueWaterfallData.data.flatRows.filter(
                (row: any) =>
                  row.customerId === data.id || row.label === data.legalName
              )
            if (customerRows.length > 0) {
              contextString += `- Revenue Data:\n`
              customerRows.forEach((row: any) => {
                contextString += `  • ${row.label}: Booked ${formatCurrency(
                  row.bookedTotal,
                  contextData.currency
                )}, Recognized ${formatCurrency(
                  row.recognized,
                  contextData.currency
                )}, Remaining ${formatCurrency(
                  row.remaining,
                  contextData.currency
                )}\n`
              })
            }
          }
        } else if (item.type === 'product') {
          const data = item.data
          contextString += `- Name: ${data.name}\n`
          contextString += `- Label: ${data.label || 'N/A'}\n`
          contextString += `- ID: ${data.id}\n`

          // Try to find revenue data for this specific product
          if (contextData.revenueWaterfallData?.data?.flatRows) {
            const productRows =
              contextData.revenueWaterfallData.data.flatRows.filter(
                (row: any) =>
                  row.label?.includes(data.name) ||
                  row.label?.includes(data.label)
              )
            if (productRows.length > 0) {
              contextString += `- Revenue Data:\n`
              productRows.forEach((row: any) => {
                contextString += `  • ${row.label}: Booked ${formatCurrency(
                  row.bookedTotal,
                  contextData.currency
                )}, Recognized ${formatCurrency(
                  row.recognized,
                  contextData.currency
                )}, Remaining ${formatCurrency(
                  row.remaining,
                  contextData.currency
                )}\n`
              })
            }
          }
        } else if (item.type === 'schedule') {
          const data = item.data
          contextString += `- Schedule ID: ${data.id}\n`
          contextString += `- Customer ID: ${data.customerId || 'N/A'}\n`
        } else if (item.type.includes('_group')) {
          const data = item.data
          contextString += `- ${data.summary}\n`
          contextString += `- Count: ${data.count}\n`
        } else if (item.type === 'revenue_analysis') {
          const data = item.data
          contextString += `- Analysis Type: ${data.type}\n`
          contextString += `- Description: ${data.description}\n`
        } else if (item.type === 'table_selection') {
          const data = item.data
          contextString += `- Selection: ${data.label}\n`
          contextString += `- Summary: ${data.summary}\n`
          contextString += `- Rows: ${data.rowLabels.join(', ')}\n`
          contextString += `- Columns: ${data.columnLabels.join(', ')}\n`
          if (data.cellData && data.cellData.length > 0) {
            contextString += `- Cell Values:\n`
            data.cellData.forEach((cell: any) => {
              contextString += `  • ${cell.rowLabel} | ${cell.columnLabel}: ${cell.value}\n`
            })
          }
        }
      })
    }
  }

  return contextString
}

export const useAiChat = (contextData?: ContextData) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentStatus, setCurrentStatus] = useState<StatusType>('idle')
  const [statusMessage, setStatusMessage] = useState<string>('')
  const [error, setError] = useState<Error | undefined>()

  // Handle status updates
  useEffect(() => {
    if (isLoading) {
      if (currentStatus === 'idle') {
        setCurrentStatus('processing')
        setStatusMessage('Processing your request...')
      }
    } else if (currentStatus !== 'idle' && currentStatus !== 'error') {
      setCurrentStatus('idle')
      setStatusMessage('')
    }
  }, [isLoading, currentStatus])

  // Helper function to parse AI response for components
  const parseResponseForComponents = useCallback(
    (
      responseText: string
    ): {
      textContent: string
      components: GenerativeComponentData[]
    } => {
      // Parse fenced component blocks
      const fencedRegex = /```component\n([\s\S]*?)\n```/g
      const fenced = [...responseText.matchAll(fencedRegex)]

      // Parse inline component blocks (<component { ... } />)
      const inlineRegex = /<?component\s*\{([\s\S]*?)\}\s*\/?\>?/g
      const inline = [...responseText.matchAll(inlineRegex)]

      const components: GenerativeComponentData[] = []

      const pushComponent = (raw: string) => {
        try {
          const c = JSON.parse(raw) as GenerativeComponentData
          if ((c as any)?.type) {
            if ((c as any).type === 'chart') {
              const cc: any = { ...c }
              if (!Array.isArray(cc.data)) cc.data = []
              if (!cc.unit) cc.unit = '£'
              if (!cc.variant) cc.variant = 'bar'
              if (!cc.dateFrom) cc.dateFrom = '2024-01-01'
              if (!cc.dateTo) cc.dateTo = '2024-12-31'
              if (!Array.isArray(cc.unitTickValues))
                cc.unitTickValues = [0, 1000, 10000]
              components.push(cc)
              return
            }
            components.push(c)
          }
        } catch {}
      }

      fenced.forEach(m => pushComponent(m[1]))
      inline.forEach(m => pushComponent(`{${m[1]}}`))

      // Strip component blocks from prose (no placeholders)
      const textContent = responseText
        .replace(/```component\n[\s\S]*?\n```/g, '')
        .replace(inlineRegex, '')
        .trim()

      return { textContent, components }
    },
    []
  )

  const callGeminiAPI = async (
    userMessage: string,
    contextData?: ContextData
  ): Promise<string> => {
    const apiKey = getApiKey()

    if (!apiKey) {
      throw new Error(
        'No API key available. Please set your Gemini API key in the settings.'
      )
    }

    const ai = new GoogleGenAI({ apiKey })

    setCurrentStatus('processing')
    setStatusMessage('Processing your request...')
    await new Promise(resolve => setTimeout(resolve, 300))

    setCurrentStatus('searching')
    setStatusMessage('Searching through your data...')
    await new Promise(resolve => setTimeout(resolve, 300))

    setCurrentStatus('generating')
    setStatusMessage('Generating response...')

    try {
      // Build enhanced system instruction with context
      const contextInfo = formatContextForLLM(contextData)

      // Optional focused-customer enrichment (journals/schedules/invoices counts)
      let enrichmentBlock = ''
      const focus = getFocusedCustomer(contextData)
      if (focus.id) {
        const filters = (contextData as any)?.revenueWaterfallFilters
        const from = filters?.bookedFrom || filters?.recognizedFrom
        const to = filters?.recognizedUpTo || filters?.bookedTo
        const currency = (contextData as any)?.currency
        try {
          const enriched = await fetchAllForCustomer({
            customerId: focus.id,
            from,
            to,
            currency
          })
          const invCount = enriched?.invoices?.length || 0
          const jrnlCount = enriched?.journals?.length || 0
          const schCount = enriched?.schedules?.length || 0
          enrichmentBlock = `\n### Customer Enrichment (Focused)\n- Invoices: ${invCount}\n- Journals: ${jrnlCount}\n- Billing Schedules: ${schCount}\n`
        } catch {}
      }

      const promptPrefix = `
                ${getCustomSystemPrompt()}
                <generative_ui_context>
                ${generativeUIContext}
                </generative_ui_context>
                ${contextInfo}
                ${enrichmentBlock}
        `

      // Enhance user message with focus directive if available
      let enhancedMessage = userMessage
      if (focus.id) {
        enhancedMessage = `${userMessage}\n\nFocus customer: ${focus.id}${focus.name ? ` (${focus.name})` : ''}. Do not ask the user to choose a customer; use this one.`
      } else if (
        contextData?.selectedContextItems &&
        contextData.selectedContextItems.size > 0
      ) {
        const contextItemsArray = Array.from(contextData.selectedContextItems)
        enhancedMessage = `${userMessage}\n\n(Note: User has selected these context items: ${contextItemsArray.join(', ')})`
      }

      const response = await ai.models.generateContent({
        model: getSelectedModel(),
        contents: [`${promptPrefix}\n\nUser request:\n${enhancedMessage}`]
      })
      return response.text ?? ''
    } catch (err) {
      console.error('Google Gemini API Error:', err)
      throw new Error(
        'Failed to get response from AI assistant. Please check your API key and try again.'
      )
    }
  }

  const simulateAIResponse = useCallback(
    async (userMessage: string) => {
      setIsLoading(true)
      setError(undefined)

      try {
        const canned = detectCannedResponse(userMessage, contextData as any)
        if (canned) {
          setCurrentStatus('processing')
          setStatusMessage('Processing your request...')
          await new Promise(r => setTimeout(r, 2000))
          setCurrentStatus('generating')
          setStatusMessage('Preparing answer...')
          await new Promise(r => setTimeout(r, 3000))

          const assistantMessage: Message = {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: canned.prose,
            createdAt: new Date(),
            components: canned.components ?? [],
            type:
              canned.components && canned.components.length > 0
                ? 'component'
                : 'text'
          }
          setMessages(prev => [...prev, assistantMessage])
          setCurrentStatus('idle')
          setStatusMessage('')
          return
        }

        const responseContent = await callGeminiAPI(userMessage, contextData)
        const { textContent, components } =
          parseResponseForComponents(responseContent)

        const assistantMessage: Message = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: textContent,
          createdAt: new Date(),
          components: components,
          type: components.length > 0 ? 'component' : 'text'
        }

        setMessages(prevMessages => [...prevMessages, assistantMessage])
        setCurrentStatus('idle')
        setStatusMessage('')
      } catch (err) {
        setCurrentStatus('error')
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Something went wrong. Please try again.'
        setStatusMessage(errorMessage)
        setError(err instanceof Error ? err : new Error(errorMessage))
      } finally {
        setIsLoading(false)
      }
    },
    [contextData, parseResponseForComponents]
  )

  const handleSubmit = useCallback(
    (message: string) => {
      if (message.trim() && !isLoading) {
        const messageContent = message.trim()

        const userMessage: Message = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: messageContent,
          createdAt: new Date()
        }

        setMessages(prevMessages => [...prevMessages, userMessage])
        simulateAIResponse(messageContent)
      }
    },
    [isLoading, simulateAIResponse]
  )

  const sendMessage = useCallback(
    (content: string) => {
      if (content.trim() && !isLoading) {
        const messageContent = content.trim()

        const userMessage: Message = {
          id: `user-${Date.now()}`,
          role: 'user',
          content: messageContent,
          createdAt: new Date()
        }

        setMessages(prevMessages => [...prevMessages, userMessage])
        simulateAIResponse(messageContent)
      }
    },
    [isLoading, simulateAIResponse]
  )

  const clearError = useCallback(() => {
    setCurrentStatus('idle')
    setStatusMessage('')
    setError(undefined)
  }, [])

  const reload = useCallback(() => {
    if (messages.length > 0) {
      const lastUserMessage = [...messages]
        .reverse()
        .find(m => m.role === 'user')
      if (lastUserMessage) {
        // Remove the last assistant message if any
        const filteredMessages = messages.filter((m, index) => {
          const isLastAssistantMessage =
            m.role === 'assistant' && index === messages.length - 1
          return !isLastAssistantMessage
        })
        setMessages(filteredMessages)

        // Regenerate response
        simulateAIResponse(lastUserMessage.content)
      }
    }
  }, [messages, simulateAIResponse])

  const stop = useCallback(() => {
    setIsLoading(false)
    setCurrentStatus('idle')
    setStatusMessage('')
  }, [])

  return {
    // Chat state
    messages,
    isLoading,
    error,

    // Status indicators
    currentStatus,
    statusMessage,

    // Actions
    handleSubmit,
    sendMessage,
    setMessages,
    reload,
    stop,
    clearError
  }
}
